/**
 * Async SearchableSelect — gọi API search khi user gõ.
 * Dùng cho dropdown có nhiều data (companies 2600+, contacts 46000+).
 *
 * Props:
 *  - searchApi: string — endpoint VD: '/crm/companies'
 *  - searchParam: string — query param name, default 'search'
 *  - labelField: string | function — field tên hoặc function(item) → label
 *  - subField: string — field phụ (VD: 'tax_code')
 *  - value: string — selected ID
 *  - onChange: (id) => void
 *  - placeholder: string
 *  - preloadValue: boolean — load item theo value khi mount
 *  - renderOption: (item, search) => JSX — thay CẢ khối label+sub của MỘT dòng trong danh sách
 *    (ô đã chọn vẫn dùng labelField). Dùng khi một dòng cần nhiều hơn "tên + 1 dòng phụ",
 *    vd chọn sản phẩm phải thấy mã · danh mục · giá · chu kỳ mới phân biệt được — xem
 *    components/crm/ProductPicker.jsx (renderProductOption).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, ChevronDown, Loader2 } from 'lucide-react';
import api from '@host/axios';

import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { i18n } from '../../i18n/runtime.js';

export default function AsyncSearchableSelect({
  searchApi, searchParam = 'search', labelField = 'name', subField,
  value, onChange, placeholder = i18n._(msg`Tìm kiếm...`), disabled = false,
  extraParams = {}, limit = 20, renderTag, renderOption,
}) {
  const {
    t
  } = useLingui();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const ref = useRef(null);
  const portalRef = useRef(null);
  const debounceRef = useRef(null);

  const getLabel = useCallback((item) => {
    if (typeof labelField === 'function') return labelField(item);
    return item[labelField] || item.name || item.id;
  }, [labelField]);

  // Load selected item label + cache item object
  useEffect(() => {
    if (!value) { setSelectedLabel(''); setSelectedItem(null); return; }
    // Try from current options
    const found = options.find(o => o.id === value);
    if (found) { setSelectedLabel(getLabel(found)); setSelectedItem(found); return; }
    // Load from API
    api.get(`${searchApi}/${value}`).then(r => {
      const item = r.data.data;
      if (item) { setSelectedLabel(getLabel(item)); setSelectedItem(item); }
    }).catch(() => {});
  }, [value]);

  // Search
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { ...extraParams, limit, [searchParam]: search || '' };
        const res = await api.get(searchApi, { params });
        let list = res.data.data || [];
        // Đảm bảo selected item luôn xuất hiện đầu tiên (dù không có trong top N).
        if (!search && value && selectedItem && !list.some(o => o.id === value)) {
          list = [selectedItem, ...list];
        } else if (!search && value) {
          // Pin selected item lên đầu nếu đã có trong list
          list = list.slice().sort((a, b) => {
            if (a.id === value) return -1;
            if (b.id === value) return 1;
            return 0;
          });
        }
        setOptions(list);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, search ? 300 : 0); // debounce khi gõ, load ngay khi mở
    return () => clearTimeout(debounceRef.current);
  }, [search, open, searchApi, value, selectedItem]);

  // Click outside
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target) && !(portalRef.current && portalRef.current.contains(e.target))) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (item) => {
    onChange(item.id, item);
    setSelectedLabel(getLabel(item));
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSelectedLabel('');
  };

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <div onClick={() => !disabled && setOpen(!open)}
        className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-xl text-sm cursor-pointer transition min-h-[42px] ${
          disabled ? 'bg-gray-50 cursor-not-allowed border-gray-200' :
          open ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-300 hover:border-gray-400'
        }`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className={`${selectedLabel ? 'text-gray-900' : 'text-gray-400'} truncate`}>
            {selectedLabel || placeholder}
          </span>
          {selectedLabel && renderTag && selectedItem && renderTag(selectedItem)}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {value && !disabled && (
            <button onClick={handleClear} className="p-0.5 text-gray-400 hover:text-red-500"><X size={13} /></button>
          )}
          <ChevronDown size={14} className={`text-gray-400 transition ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown — portal to body to avoid z-index issues in modals */}
      {open && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[99998]" onClick={() => { setOpen(false); setSearch(''); }}>
        <div ref={portalRef} className="fixed z-[99999] bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
          style={(() => { const r = ref.current?.getBoundingClientRect(); if (!r) return {}; const below = window.innerHeight - r.bottom; return { top: below > 280 ? r.bottom + 4 : r.top - 260, left: r.left, width: r.width }; })()}>
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400 shrink-0" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} autoFocus
                placeholder={placeholder} className="flex-1 text-sm bg-transparent outline-none" />
              {loading && <Loader2 size={14} className="text-blue-500 animate-spin shrink-0" />}
            </div>
          </div>

          {/* Options */}
          <div className="max-h-56 overflow-y-auto">
            {options.length === 0 && !loading && (
              <p className="text-center text-xs text-gray-400 py-4">{search ? t`Không tìm thấy` : t`Gõ để tìm kiếm...`}</p>
            )}
            {options.map(item => {
              const label = getLabel(item);
              const sub = subField ? item[subField] : null;
              const isSelected = item.id === value;
              return (
                <button key={item.id} type="button" onClick={() => handleSelect(item)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-blue-50 transition ${isSelected ? 'bg-blue-50' : ''}`}>
                  <div className={`flex-1 min-w-0 ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                    {renderOption ? renderOption(item, search) : (
                      <>
                        <p className={`text-sm truncate ${isSelected ? 'font-medium' : ''}`}>{label}</p>
                        {sub && <p className="text-[10px] text-gray-400 truncate">{sub}</p>}
                      </>
                    )}
                  </div>
                  {renderTag && renderTag(item)}
                </button>
              );
            })}
          </div>
        </div>
        </div>,
        document.body
      )}
    </div>
  );
}
